import { AxiosInstance } from "axios";
import { ApiRouteHelpers as Helpers } from "../../../root/apiRoutes";
import {
  AddOrUpdatePrivacyCenterPolicySection,
  CreatePrivacyCenter,
  FormCustomFieldSource,
  PrivacyCenterDto,
  PrivacyCenterDomainDto,
  PrivacyCenterFontDto,
  PrivacyCenterPolicyDto,
  PrivacyCenterPolicySectionDto,
  UpdatePrivacyCenter,
  UpdatePrivacyCenterPolicy,
} from "../dto/PrivacyCenterDto";

import {
  OrganizationPublicId,
  PrivacyCenterImageType,
  PrivacyCenterPublicId,
  PrivacyPolicySectionPublicId,
} from "../Types";
import { CMPSettingsDto, CMPStagingSettingsDto } from "../dto/CMPSettingsDto";
import {
  PrivacyCenterAvailableIntegrationVendorIdsDto,
  PrivacyCenterIntegrationsDto,
} from "../dto/PrivacyCenterIntegrationsDto";

export type UpdatePCGoogleAnalyticsMeasurementIdDto = {
  gaMeasurementId: string;
};

export type UpsertPrivacyCenterDomain = {
  privacyCenterId: PrivacyCenterPublicId;
  customDomain: string;
  letsEncryptChallengeKey?: string | null;
  letsEncryptChallengeToken?: string | null;
  certificateStatus?: string | null;
  expiresAt?: string | null;
};
import { PrivacyCenterNoticeController } from "./PrivacyCenterNoticeController";

export class PrivacyCenterController {
  constructor(private axios: AxiosInstance) {}

  public updateFlowDownField = async (
    organizationId: OrganizationPublicId,
    source: FormCustomFieldSource,
    fieldKey: string,
    includeInVendorRequests: boolean,
  ) => {
    const url = `${Helpers.organization.privacyCenters(
      organizationId,
    )}/flowDownFields/${source}/${encodeURIComponent(fieldKey)}`;
    await this.axios.put(url, { includeInVendorRequests });
  };

  public getPrivacyCenters = async (organizationId: OrganizationPublicId) => {
    const url = Helpers.organization.privacyCenters(organizationId);
    const response = await this.axios.get<PrivacyCenterDto[]>(url);
    return response.data as PrivacyCenterDto[];
  };

  public getPrivacyCenter = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.detail(organizationId, privacyCenterId);
    const response = await this.axios.get<PrivacyCenterDto>(url);
    return response.data as PrivacyCenterDto;
  };

  public getPrivacyCenterFonts = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.fonts(organizationId, privacyCenterId);
    const response = await this.axios.get<PrivacyCenterFontDto[]>(url);
    return response.data as PrivacyCenterFontDto[];
  };

  public updatePrivacyCenter = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    updatePrivacyCenter: UpdatePrivacyCenter,
  ) => {
    const url = Helpers.organization.privacyCenter.detail(organizationId, privacyCenterId);
    const response = await this.axios.put<PrivacyCenterDto>(url, updatePrivacyCenter);
    return response.data as PrivacyCenterDto;
  };

  public deletePrivacyCenterImage = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    imageType: PrivacyCenterImageType,
  ) => {
    const url = Helpers.organization.privacyCenter.deleteImage(
      organizationId,
      privacyCenterId,
      imageType,
    );
    const response = await this.axios.post<PrivacyCenterDto>(url);
    return response.data as PrivacyCenterDto;
  };

  public createPrivacyCenter = async (
    organizationId: OrganizationPublicId,
    createPrivacyCenter: CreatePrivacyCenter,
  ) => {
    const url = Helpers.organization.privacyCenters(organizationId);
    const response = await this.axios.post<PrivacyCenterDto>(url, createPrivacyCenter);
    return response.data as PrivacyCenterDto;
  };

  // PRIVACY POLICY ENDPOINTS

  public getPrivacyCenterPolicy = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.privacyPolicy.policy(
      organizationId,
      privacyCenterId,
    );

    const response = await this.axios.get<PrivacyCenterPolicyDto>(url);
    return response.data as PrivacyCenterPolicyDto;
  };

  public updatePrivacyCenterPolicy = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    updatePolicy: UpdatePrivacyCenterPolicy,
  ) => {
    const url = Helpers.organization.privacyCenter.privacyPolicy.policy(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.put<PrivacyCenterPolicyDto>(url, updatePolicy);
    return response.data as PrivacyCenterPolicyDto;
  };

  public addPrivacyCenterPolicySection = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    addSection: AddOrUpdatePrivacyCenterPolicySection,
  ) => {
    const url = Helpers.organization.privacyCenter.privacyPolicy.sections(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.post<PrivacyCenterPolicySectionDto>(url, addSection);
    return response.data as PrivacyCenterPolicySectionDto;
  };

  public updatePrivacyCenterPolicySection = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    sectionId: PrivacyPolicySectionPublicId,
    updateSection: AddOrUpdatePrivacyCenterPolicySection,
  ) => {
    const url = Helpers.organization.privacyCenter.privacyPolicy.section.detail(
      organizationId,
      privacyCenterId,
      sectionId,
    );
    const response = await this.axios.put<PrivacyCenterPolicySectionDto>(url, updateSection);
    return response.data as PrivacyCenterPolicySectionDto;
  };

  public deletePrivacyCenterPolicySection = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    sectionId: PrivacyPolicySectionPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.privacyPolicy.section.detail(
      organizationId,
      privacyCenterId,
      sectionId,
    );
    const response = await this.axios.delete<PrivacyCenterPolicySectionDto>(url);
    return response.data as PrivacyCenterPolicySectionDto;
  };

  public verifyPrivacyCenterDomain = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    domain: string,
  ) => {
    const url = Helpers.organization.privacyCenter.verifyDomain(
      organizationId,
      privacyCenterId,
      domain,
    );
    const response = await this.axios.get<boolean>(url);
    return response.data;
  };

  public createPrivacyCenterDomain = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    payload: UpsertPrivacyCenterDomain,
  ) => {
    const url = Helpers.organization.privacyCenter.domain(organizationId, privacyCenterId);
    const response = await this.axios.post<PrivacyCenterDomainDto>(url, payload);
    return response.data as PrivacyCenterDomainDto;
  };

  public updatePrivacyCenterDomain = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    payload: UpsertPrivacyCenterDomain,
  ) => {
    const url = Helpers.organization.privacyCenter.domain(organizationId, privacyCenterId);
    const response = await this.axios.put<PrivacyCenterDomainDto>(url, payload);
    return response.data as PrivacyCenterDomainDto;
  };

  public postPrivacyCenterFont = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    font: File,
  ): Promise<PrivacyCenterFontDto> => {
    const url = Helpers.organization.privacyCenter.fonts(organizationId, privacyCenterId);
    const formData = new FormData();
    formData.append("font", font, font.name);
    const response = await this.axios.post<PrivacyCenterFontDto>(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data as PrivacyCenterFontDto;
  };

  public getPrivacyCenterCMPCustomization = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.cmpCustomization(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.get<CMPSettingsDto>(url);
    return response.data as CMPSettingsDto;
  };

  public getPrivacyCenterCMPStagingCustomization = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.cmpCustomizationStaging(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.get<CMPStagingSettingsDto>(url);
    return response.data as CMPStagingSettingsDto;
  };

  public postPrivacyCenterCMPCustomization = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    incomingCustomization: CMPSettingsDto,
  ) => {
    const url = Helpers.organization.privacyCenter.cmpCustomization(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.post<CMPSettingsDto>(url, incomingCustomization);
    return response.data as CMPSettingsDto;
  };

  public updateGaMeasurementId = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    update: UpdatePCGoogleAnalyticsMeasurementIdDto,
  ) => {
    const url = Helpers.organization.privacyCenter.updateGAMeasurementId(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.post<UpdatePCGoogleAnalyticsMeasurementIdDto>(url, update);
    return response.data as UpdatePCGoogleAnalyticsMeasurementIdDto;
  };

  public notices = new PrivacyCenterNoticeController(this.axios);

  public getPrivacyCenterIntegrations = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.integrations(organizationId, privacyCenterId);
    const response = await this.axios.get<PrivacyCenterIntegrationsDto>(url);
    return response.data as PrivacyCenterIntegrationsDto;
  };

  public getAvailableIntegrationVendorIds = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.availableIntegrationVendorIds(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.get<PrivacyCenterAvailableIntegrationVendorIdsDto>(url);
    return response.data as PrivacyCenterAvailableIntegrationVendorIdsDto;
  };

  public getConsentRecordsStats = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
  ) => {
    const url = Helpers.organization.privacyCenter.consentRecordsStats(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.get<any>(url);
    return response.data;
  };

  public getConsentRecords = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    params?: {
      page?: number;
      pageSize?: number;
      sort?: string;
    },
  ) => {
    const url = Helpers.organization.privacyCenter.consentRecords(organizationId, privacyCenterId);
    const response = await this.axios.get<any>(url, { params });
    return response.data;
  };

  public getConsentRecordsCsv = async (
    organizationId: OrganizationPublicId,
    privacyCenterId: PrivacyCenterPublicId,
    params?: {
      sort?: string;
    },
  ) => {
    const url = Helpers.organization.privacyCenter.consentRecordsCsv(
      organizationId,
      privacyCenterId,
    );
    const response = await this.axios.get<any>(url, { params, responseType: "text" });
    return response.data;
  };
}
